import React from 'react';
import {Greeting, GreetingData} from "types/Greeting";
import {Form, Formik, FormikHelpers} from "formik";
import Field from "components/Form/Field";
import FormControls from "components/FormControls";
import Toggle from "components/Form/Toggle";
import Uploader from "../../../components/Form/Uploader";
import * as Yup from "yup";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";
import {TextAreaField} from "../../../components/Form/Form";

type Props = {
    greeting?: Greeting | null
    onSubmit: (values: GreetingData, helpers: FormikHelpers<GreetingData>) => any
}

function GreetingForm(props: Props) {
    const validationSchema = Yup.object().shape({
      name: Yup.string().required("This field is required").max(250, "Maximum character limit exceeded"),
      title: Yup.string().required("This field is required").max(250, "Maximum character limit exceeded"),
      body: Yup.string().max(20000, "Maximum character limit exceeded"),
      is_enabled: Yup.boolean(),
    })

    const initialValues: GreetingData = {
        name: props.greeting?.name || '',
        title: props.greeting?.title || '',
        body: props.greeting?.body || '',
        media: props.greeting?.media.map(file => ({id: file.id})) || [],
        is_enabled: props.greeting?.is_enabled ?? false,
    }

    return (
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={props.onSubmit}>
            {formik => {
              return (
                <Form className="space-y-8 divide-y divide-gray-200">
                    <ErrorNotification formik={formik} />

                    <div className="shadow sm:rounded-md">
                        <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-md">
                            <div className="space-y-8 divide-y divide-gray-200">
                                <div>
                                    <div>
                                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                                            Greeting Content
                                        </h3>
                                        <p className="mt-1 text-sm text-gray-500">
                                            The main content of the greeting
                                        </p>
                                    </div>
                                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">

                                        {/* Name */}
                                        <div className="sm:col-span-4 xl:col-span-3">
                                            <Field
                                                label="Name *"
                                                hint="For internal use. 250 chars max."
                                                type="text"
                                                name="name"
                                                maxLength={250}
                                            />
                                        </div>

                                        {/* Title */}
                                        <div className="sm:col-span-4 sm:col-start-1 xl:col-span-3 xl:col-start-1">
                                            <Field
                                                label="Title *"
                                                hint="250 chars max"
                                                type="text"
                                                name="title"
                                                maxLength={250}
                                            />
                                        </div>

                                        {/* Body */}
                                        <div className="sm:col-span-6">
                                            <TextAreaField
                                              name='body'
                                              label="Body"
                                              hint="Optional. 20,000 chars max."
                                              autoComplete="off"
                                              maxChars={20000}
                                              rows={10}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Media */}
                                <div className="pt-8">
                                    <div>
                                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                                            Media
                                        </h3>
                                        <p className="mt-1 text-sm text-gray-500">
                                            Images, audio and video that's relevant to this greeting
                                        </p>
                                    </div>
                                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                        {/* Files */}
                                        <div className="sm:col-span-6">
                                            <Uploader
                                                type={[Uploader.types.IMAGE, Uploader.types.AUDIO, Uploader.types.VIDEO]}
                                                files={props.greeting?.media || []}
                                                onChange={files => formik.setFieldValue('media', files.map(file => ({id: file.id})))}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-8">
                                    <div>
                                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                                            Visibility
                                        </h3>
                                        <p className="mt-1 text-sm text-gray-500">
                                            Choose whether to enable this greeting
                                        </p>
                                    </div>
                                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                        <div className="sm:col-span-2 lg:col-span-1 mt-1">
                                            <Toggle
                                                label="Enable"
                                                checked={formik.values.is_enabled}
                                                onChange={checked => formik.setFieldValue('is_enabled', checked)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <FormControls formik={formik} />
                    </div>
                </Form>
            )}}
        </Formik>
    );
}

export default GreetingForm;